/* AgentMeeting — Billing & Usage dashboard */

import React from 'react';
import { MarketingBar } from '../shared/marketing-bar';
import { billing as billingApi, HTTPError } from '../shared/api';
import { useAuth } from '../shared/auth';

interface BillingStatus {
  plan: string;
  credits_cents: number;
  stripe_customer_id?: string;
  subscription_id?: string;
  current_period_end?: string;
}

type PlanTier = 'hobby' | 'pro' | 'team' | 'enterprise';

interface PlanDef {
  id: PlanTier;
  name: string;
  price: string;
  priceUnit: string;
  blurb: string;
  features: string[];
  checkoutId?: 'pro' | 'team' | 'enterprise';
}

const PLANS: PlanDef[] = [
  {
    id: 'hobby',
    name: 'Hobby',
    price: '$0',
    priceUnit: '/ month',
    blurb: '随便玩玩 · BYOK',
    features: ['100 min / month', 'BYOK only', '3 agents max', 'community support'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$29',
    priceUnit: '/ month',
    blurb: '认真开会的个人',
    features: ['1500 min / month', '$30 LLM credits', '8 agents max', 'priority support'],
    checkoutId: 'pro',
  },
  {
    id: 'team',
    name: 'Team',
    price: '$99',
    priceUnit: '/ month',
    blurb: '小团队共享',
    features: ['6000 min / month', '$120 LLM credits', '20 agents max', '5 seats included'],
    checkoutId: 'team',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Contact',
    priceUnit: 'us',
    blurb: '定制 SLA / SSO',
    features: ['unlimited min', 'pooled credits', 'SSO + audit log', 'dedicated CSM'],
    checkoutId: 'enterprise',
  },
];

function formatCents(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}

export function Billing({ goTo }) {
  const { user } = useAuth();
  const [tab, setTab] = React.useState('overview');
  const [status, setStatus] = React.useState<BillingStatus | null>(null);
  const [stripeOk, setStripeOk] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [actionErr, setActionErr] = React.useState<string>('');
  const [busyPlan, setBusyPlan] = React.useState<string>('');
  const [portalBusy, setPortalBusy] = React.useState(false);

  // Detect ?status=success in either the location.hash (hash-router style
  // like #/billing?status=success) or in regular query string.
  const successBanner = React.useMemo(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return /[?&]status=success/.test(hash) || /[?&]status=success/.test(search);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await billingApi.status();
        if (cancelled) return;
        setStatus(s);
        setStripeOk(true);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HTTPError && (err.status === 404 || err.status >= 500)) {
          setStripeOk(false);
        } else if (err instanceof HTTPError) {
          // Other HTTP errors (401, etc) — still fall back to user data,
          // but don't claim Stripe is unconfigured.
          setStripeOk(true);
        } else {
          setStripeOk(false);
        }
        // Fallback: build a synthetic status from the auth context.
        if (user) {
          setStatus({
            plan: user.plan || 'hobby',
            credits_cents: user.credits_cents || 0,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const onCheckout = async (planId: 'pro' | 'team' | 'enterprise') => {
    setActionErr('');
    setBusyPlan(planId);
    try {
      const { url } = await billingApi.checkout(planId);
      window.location.href = url;
    } catch (err) {
      if (err instanceof HTTPError) {
        setActionErr(`Checkout failed (HTTP ${err.status}). Stripe may not be configured.`);
      } else {
        setActionErr('Checkout failed. Please try again.');
      }
      setBusyPlan('');
    }
  };

  const onPortal = async () => {
    setActionErr('');
    setPortalBusy(true);
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch (err) {
      if (err instanceof HTTPError) {
        setActionErr(`Portal unavailable (HTTP ${err.status}).`);
      } else {
        setActionErr('Portal unavailable. Please try again.');
      }
      setPortalBusy(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'usage', label: '用量明细' },
    { id: 'invoices', label: '发票 / 账单' },
    { id: 'payment', label: '支付方式' },
    { id: 'plan', label: '套餐' },
  ];

  const currentPlan = (status?.plan || user?.plan || 'hobby').toLowerCase();
  const credits = status?.credits_cents ?? user?.credits_cents ?? 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--fg-0)' }}>
      <MarketingBar here={null} goTo={goTo} signedIn={true} />

      <div style={{ padding: '32px 48px', maxWidth: 1200, margin: '0 auto' }}>
        {successBanner && (
          <div style={{
            border: '1px solid var(--live)', borderRadius: 6,
            background: 'var(--live-dim)', padding: '12px 16px',
            marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span className="mono" style={{ color: 'var(--live)', fontSize: 12, letterSpacing: '0.08em' }}>
              [✓ SUBSCRIPTION ACTIVE]
            </span>
            <span style={{ fontSize: 13, color: 'var(--fg-0)' }}>
              Subscription active! Stripe webhook may take a few seconds to sync your plan.
            </span>
          </div>
        )}

        {!stripeOk && !loading && (
          <div style={{
            border: '1px dashed var(--judge)', borderRadius: 6,
            padding: '12px 16px', marginBottom: 16, background: 'var(--bg-1)',
          }}>
            <div className="mono tiny" style={{ color: 'var(--judge)', letterSpacing: '0.1em' }}>
              [STRIPE NOT CONFIGURED]
            </div>
            <div style={{ fontSize: 13, color: 'var(--fg-1)', marginTop: 4, lineHeight: 1.5 }}>
              Live billing is unavailable. Showing data from your account record.
              Self-hosted users: set <code className="mono" style={{ color: 'var(--live)' }}>STRIPE_SECRET_KEY</code> on the backend to enable upgrades.
            </div>
          </div>
        )}

        {actionErr && (
          <div style={{
            border: '1px solid var(--err)', borderRadius: 6,
            padding: '10px 14px', marginBottom: 16, background: 'var(--bg-1)',
            color: 'var(--err)', fontSize: 13,
          }}>
            {actionErr}
          </div>
        )}

        <div className="flex between" style={{ alignItems: 'flex-end', marginBottom: 24 }}>
          <div>
            <span className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
              [BILLING & USAGE]
            </span>
            <h1 style={{ fontSize: 32, fontWeight: 500, letterSpacing: '-0.02em', margin: '8px 0 4px' }}>
              你的会议账本
            </h1>
            <p className="meta" style={{ margin: 0 }}>
              当前套餐 · <span style={{ color: 'var(--fg-0)' }}>{currentPlan}</span>
              {status?.current_period_end && (
                <> · 续费于 {formatDate(status.current_period_end)}</>
              )}
              {' · '}计费币种 USD
            </p>
          </div>
          <div className="flex gap-2">
            {status?.subscription_id && (
              <button
                className="btn primary mono"
                onClick={onPortal}
                disabled={portalBusy}
              >
                {portalBusy ? '$ opening...' : '$ manage subscription'}
              </button>
            )}
            <button className="btn ghost mono" onClick={() => goTo('settings')}>
              ⚙ settings
            </button>
          </div>
        </div>

        {/* tabs */}
        <div className="flex" style={{
          gap: 0, borderBottom: '1px solid var(--line-1)', marginBottom: 24,
        }}>
          {tabs.map(t => {
            const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '10px 18px', background: 'transparent',
                border: 'none', borderBottom: '2px solid ' + (on ? 'var(--live)' : 'transparent'),
                color: on ? 'var(--fg-0)' : 'var(--fg-2)',
                cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 12,
                letterSpacing: '0.06em',
              }}>
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'overview' && (
          <BillingOverview
            currentPlan={currentPlan}
            credits={credits}
            status={status}
          />
        )}
        {tab === 'usage' && <BillingUsage />}
        {tab === 'invoices' && <BillingInvoices stripeOk={stripeOk} />}
        {tab === 'payment' && <BillingPayment stripeOk={stripeOk} hasSubscription={!!status?.subscription_id} onPortal={onPortal} portalBusy={portalBusy} />}
        {tab === 'plan' && (
          <BillingPlan
            goTo={goTo}
            currentPlan={currentPlan}
            status={status}
            onCheckout={onCheckout}
            onPortal={onPortal}
            busyPlan={busyPlan}
            portalBusy={portalBusy}
            stripeOk={stripeOk}
          />
        )}
      </div>
    </div>
  );
}

function BillingOverview({
  currentPlan,
  credits,
  status,
}: {
  currentPlan: string;
  credits: number;
  status: BillingStatus | null;
}) {
  // Mocked usage — backend not wired yet for these specific stats.
  const used = 743;
  const total = 1500;
  const creditsTotal = 3000; // cents

  return (
    <div className="col gap-4">
      {/* top stats */}
      <div className="grid-4">
        <StatCard
          tag="MEETING MINUTES"
          value={`${used}`}
          unit={`/ ${total} min`}
          accent="var(--live)"
          progress={used / total}
          sub={`本月已开 24 场会 · 平均 31 min/场`}
          comingSoon
        />
        <StatCard
          tag="LLM CREDITS"
          value={formatCents(credits)}
          unit={`of ${formatCents(creditsTotal)}`}
          accent="var(--judge)"
          progress={Math.min(1, credits / creditsTotal)}
          sub={`实时余额 · 来自后端`}
        />
        <StatCard
          tag="OVERAGE"
          value="$0.00"
          unit="this cycle"
          accent="var(--neutral)"
          progress={0}
          sub="超额按 $0.04/min · 当前 0 min"
          comingSoon
        />
        <StatCard
          tag="NEXT INVOICE"
          value={status?.subscription_id ? '$29.00' : '—'}
          unit={status?.current_period_end ? `due ${formatDate(status.current_period_end)}` : 'no active sub'}
          accent="var(--fg-0)"
          progress={1}
          sub={`${currentPlan} · ${status?.subscription_id ? '已绑卡' : '未订阅'}`}
        />
      </div>

      {/* burn-down chart */}
      <div style={{
        border: '1px solid var(--line-2)', borderRadius: 6,
        background: 'var(--bg-1)', padding: 20,
      }}>
        <div className="flex between center" style={{ marginBottom: 16 }}>
          <div>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
              [USAGE BURN-DOWN]
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>
              这个月你烧得快还是慢
            </div>
          </div>
          <div className="flex gap-2 mono tiny">
            <span className="tag" style={{ borderColor: 'var(--judge)', color: 'var(--judge)' }}>coming soon</span>
            <span className="tag" style={{ borderColor: 'var(--live)', color: 'var(--live)' }}>actual</span>
            <span className="tag dim">linear projection</span>
          </div>
        </div>
        <BurnDownChart />
        <div className="flex between mono tiny dim" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line-1)' }}>
          <span>按当前速度, 月底将用 <span style={{ color: 'var(--live)' }}>1182 min</span> · 不会超额</span>
          <span>预计花费 ${(credits / 100 * 30 / 19).toFixed(2)} LLM credits</span>
        </div>
      </div>

      {/* recent meetings */}
      <div style={{
        border: '1px solid var(--line-2)', borderRadius: 6,
        background: 'var(--bg-1)', overflow: 'hidden',
      }}>
        <div className="flex between center" style={{
          padding: '14px 18px', borderBottom: '1px solid var(--line-1)',
          background: 'var(--bg-0)',
        }}>
          <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
            [RECENT MEETINGS]
          </div>
          <span className="tag mono tiny" style={{ borderColor: 'var(--judge)', color: 'var(--judge)' }}>coming soon</span>
        </div>
        <RecentMeetings />
      </div>
    </div>
  );
}

function StatCard({ tag, value, unit, accent, progress, sub, comingSoon }: {
  tag: string;
  value: string;
  unit: string;
  accent: string;
  progress: number;
  sub: string;
  comingSoon?: boolean;
}) {
  return (
    <div style={{
      padding: 16, border: '1px solid var(--line-2)',
      borderRadius: 6, background: 'var(--bg-1)', position: 'relative',
    }}>
      <div className="flex between" style={{ alignItems: 'center' }}>
        <div className="mono tiny" style={{ color: accent, letterSpacing: '0.1em' }}>
          [{tag}]
        </div>
        {comingSoon && (
          <span className="mono tiny dim" style={{ fontSize: 9, letterSpacing: '0.08em' }}>soon</span>
        )}
      </div>
      <div className="flex" style={{ alignItems: 'baseline', gap: 6, marginTop: 8 }}>
        <span style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em' }}>{value}</span>
        <span className="mono tiny dim">{unit}</span>
      </div>
      <div style={{
        height: 4, marginTop: 10, borderRadius: 2,
        background: 'var(--bg-3)', overflow: 'hidden',
      }}>
        <div style={{
          width: Math.min(100, progress * 100) + '%', height: '100%',
          background: accent,
          boxShadow: progress > 0 ? '0 0 6px ' + accent : 'none',
        }} />
      </div>
      <div className="meta" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
        {sub}
      </div>
    </div>
  );
}

function BurnDownChart() {
  const data = [
    [1, 28], [2, 65], [3, 89], [4, 120], [5, 145], [6, 178],
    [7, 210], [8, 248], [9, 281], [10, 320], [11, 365], [12, 412],
    [13, 445], [14, 488], [15, 521], [16, 565], [17, 612], [18, 678], [19, 743],
  ];
  const maxDay = 31;
  const maxMin = 1500;
  const W = 1100;
  const H = 200;
  const px = (d) => (d / maxDay) * W;
  const py = (m) => H - (m / maxMin) * H;

  const path = data.map(([d, m], i) => `${i === 0 ? 'M' : 'L'} ${px(d).toFixed(1)} ${py(m).toFixed(1)}`).join(' ');
  const projPath = `M ${px(19).toFixed(1)} ${py(743).toFixed(1)} L ${px(31).toFixed(1)} ${py(1182).toFixed(1)}`;

  return (
    <svg viewBox={`0 0 ${W} ${H + 30}`} style={{ width: '100%', height: 'auto' }}>
      {/* y-axis grid */}
      {[0, 0.25, 0.5, 0.75, 1].map(p => (
        <line key={p} x1={0} x2={W} y1={H * (1 - p)} y2={H * (1 - p)}
          stroke="var(--line-1)" strokeDasharray="2 4" />
      ))}
      {/* y labels */}
      {[0, 375, 750, 1125, 1500].map((m) => (
        <text key={m} x={4} y={H - (m / maxMin) * H - 4}
          fill="var(--fg-3)" fontSize="9" fontFamily="var(--mono)">
          {m}
        </text>
      ))}
      {/* fill area */}
      <path d={`${path} L ${px(19).toFixed(1)} ${H} L 0 ${H} Z`}
        fill="var(--live-dim)" opacity="0.3" />
      {/* actual line */}
      <path d={path} fill="none" stroke="var(--live)" strokeWidth="2" />
      {/* projection */}
      <path d={projPath} fill="none" stroke="var(--fg-2)"
        strokeWidth="1.5" strokeDasharray="4 4" />
      {/* today marker */}
      <line x1={px(19)} x2={px(19)} y1={0} y2={H}
        stroke="var(--live)" strokeWidth="1" opacity="0.4" />
      <circle cx={px(19)} cy={py(743)} r="4" fill="var(--live)" />
      <text x={px(19) + 8} y={py(743) - 8}
        fill="var(--live)" fontSize="10" fontFamily="var(--mono)">
        743 min · today
      </text>
      {/* x labels */}
      {[1, 8, 15, 22, 29].map(d => (
        <text key={d} x={px(d) - 6} y={H + 14}
          fill="var(--fg-3)" fontSize="9" fontFamily="var(--mono)">
          Jan {d}
        </text>
      ))}
    </svg>
  );
}

function RecentMeetings() {
  const rows = [
    ['#0124', '是否在 Q3 进入巴西市场?', '红蓝对抗 · 5 agents', '32 min', '$0.71', '#3 sonnet · #2 haiku', 'Jan 19'],
    ['#0123', '砍掉 30% 产品线讨论', '高管会议 · 6 agents', '47 min', '$1.18', '6× sonnet', 'Jan 18'],
    ['#0122', '新登录方案安全评审', '红蓝对抗 · 4 agents', '21 min', '$0.34', '4× sonnet', 'Jan 17'],
    ['#0121', '团队 OKR Q1 同步会', '苏格拉底 · 3 agents', '14 min', '$0.18', '3× haiku', 'Jan 17'],
    ['#0120', '剧本会 · 第二集大纲', '创意会 · 4 agents', '52 min', '$0.92', '4× sonnet', 'Jan 16'],
  ];
  return (
    <div>
      <div className="grid" style={{
        gridTemplateColumns: '70px 1fr 200px 80px 80px 140px 80px 40px',
        padding: '10px 18px', background: 'var(--bg-2)',
        borderBottom: '1px solid var(--line-1)',
        fontFamily: 'var(--mono)', fontSize: 10,
        color: 'var(--fg-3)', letterSpacing: '0.08em',
      }}>
        <div>ID</div><div>MOTION</div><div>PACK</div>
        <div>DURATION</div><div>COST</div><div>MODELS</div>
        <div>DATE</div><div></div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid hov" style={{
          gridTemplateColumns: '70px 1fr 200px 80px 80px 140px 80px 40px',
          padding: '12px 18px',
          borderBottom: i < rows.length - 1 ? '1px solid var(--line-1)' : 'none',
          fontSize: 12, alignItems: 'center', cursor: 'pointer',
        }}>
          <div className="mono dim">{r[0]}</div>
          <div style={{ color: 'var(--fg-0)' }}>{r[1]}</div>
          <div className="meta">{r[2]}</div>
          <div className="mono">{r[3]}</div>
          <div className="mono" style={{ color: 'var(--live)' }}>{r[4]}</div>
          <div className="mono dim" style={{ fontSize: 11 }}>{r[5]}</div>
          <div className="mono dim">{r[6]}</div>
          <div className="mono dim">→</div>
        </div>
      ))}
    </div>
  );
}

function BillingUsage() {
  return (
    <div className="col gap-4">
      <div style={{
        border: '1px solid var(--line-2)', borderRadius: 6,
        background: 'var(--bg-1)', padding: 20,
      }}>
        <div className="flex between" style={{ marginBottom: 16, alignItems: 'center' }}>
          <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
            [BY MODEL · THIS CYCLE]
          </div>
          <span className="tag mono tiny" style={{ borderColor: 'var(--judge)', color: 'var(--judge)' }}>coming soon</span>
        </div>
        {[
          ['Claude Sonnet 4.5', 12.31, '4.1M', '820K', 0.66],
          ['Claude Haiku 4.5', 2.84, '8.2M', '1.4M', 0.15],
          ['GPT-4o', 2.92, '1.1M', '210K', 0.16],
          ['Gemini 2.5 Pro', 0.35, '280K', '32K', 0.02],
        ].map(([m, cost, inp, out, frac]) => (
          <div key={m as string} style={{ marginBottom: 12 }}>
            <div className="flex between" style={{ marginBottom: 4, fontSize: 13 }}>
              <span>{m}</span>
              <span className="mono">${(cost as number).toFixed(2)} <span className="dim" style={{ fontSize: 11 }}>· in {inp} · out {out}</span></span>
            </div>
            <div style={{ height: 4, background: 'var(--bg-3)', borderRadius: 2 }}>
              <div style={{
                width: `${(frac as number) * 100}%`, height: '100%',
                background: 'var(--live)', borderRadius: 2,
              }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{
        border: '1px solid var(--line-2)', borderRadius: 6,
        background: 'var(--bg-1)', padding: 20,
      }}>
        <div className="flex between" style={{ marginBottom: 16, alignItems: 'center' }}>
          <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
            [BY AGENT · TOP 6 SPENDERS]
          </div>
          <span className="tag mono tiny" style={{ borderColor: 'var(--judge)', color: 'var(--judge)' }}>coming soon</span>
        </div>
        {[
          ['CEO 视角', 'var(--pro)', 4.21, 142],
          ['CFO · 财务严谨派', 'var(--finance)', 3.82, 128],
          ['用户代表', 'var(--judge)', 2.91, 198],
          ['CRO · 产品反对派', 'var(--err)', 2.74, 89],
          ['COO · 执行派', 'var(--neutral)', 1.88, 76],
          ['独立评委', '#888', 0.92, 41],
        ].map(([n, c, $$, msgs]) => (
          <div key={n as string} className="flex between center" style={{
            padding: '10px 0', borderBottom: '1px solid var(--line-1)',
          }}>
            <div className="flex gap-3 center">
              <div style={{
                width: 28, height: 28, borderRadius: '50%', background: c as string,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--bg-0)', fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 600,
              }}>{(n as string).split(' ')[0].slice(0, 2).toUpperCase()}</div>
              <span style={{ fontSize: 13 }}>{n}</span>
            </div>
            <div className="flex gap-4 mono tiny">
              <span className="dim">{msgs as number} msg</span>
              <span style={{ color: 'var(--live)', minWidth: 50, textAlign: 'right' }}>
                ${($$ as number).toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BillingInvoices({ stripeOk }: { stripeOk: boolean }) {
  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)', padding: 24,
      textAlign: 'center',
    }}>
      <div className="mono tiny" style={{ color: 'var(--judge)', letterSpacing: '0.1em', marginBottom: 8 }}>
        [INVOICES]
      </div>
      <div style={{ fontSize: 16, marginBottom: 8 }}>发票列表 · coming soon</div>
      <div className="meta" style={{ fontSize: 13, lineHeight: 1.6 }}>
        {stripeOk
          ? '请通过下方按钮在 Stripe Customer Portal 中查看 / 下载历史发票。'
          : 'Stripe 未配置, 历史发票暂不可用。'}
      </div>
      <span className="tag mono tiny" style={{
        borderColor: 'var(--judge)', color: 'var(--judge)',
        display: 'inline-block', marginTop: 16,
      }}>coming soon</span>
    </div>
  );
}

function BillingPayment({
  stripeOk,
  hasSubscription,
  onPortal,
  portalBusy,
}: {
  stripeOk: boolean;
  hasSubscription: boolean;
  onPortal: () => void;
  portalBusy: boolean;
}) {
  return (
    <div style={{
      border: '1px solid var(--line-2)', borderRadius: 6,
      background: 'var(--bg-1)', padding: 24,
      textAlign: 'center',
    }}>
      <div className="mono tiny" style={{ color: 'var(--judge)', letterSpacing: '0.1em', marginBottom: 8 }}>
        [PAYMENT METHODS]
      </div>
      <div style={{ fontSize: 16, marginBottom: 8 }}>支付方式 · 由 Stripe 托管</div>
      <div className="meta" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
        {hasSubscription
          ? '点击下方按钮跳转到 Stripe Customer Portal 管理你的卡片 / 账单地址 / 税号。'
          : (stripeOk
            ? '订阅后才能管理支付方式。前往「套餐」页选择计划。'
            : 'Stripe 未配置, 此功能暂不可用。')}
      </div>
      {hasSubscription && (
        <button className="btn primary mono" onClick={onPortal} disabled={portalBusy}>
          {portalBusy ? '$ opening...' : '$ open stripe portal →'}
        </button>
      )}
    </div>
  );
}

function BillingPlan({
  goTo,
  currentPlan,
  status,
  onCheckout,
  onPortal,
  busyPlan,
  portalBusy,
  stripeOk,
}: {
  goTo: (route: string) => void;
  currentPlan: string;
  status: BillingStatus | null;
  onCheckout: (planId: 'pro' | 'team' | 'enterprise') => void;
  onPortal: () => void;
  busyPlan: string;
  portalBusy: boolean;
  stripeOk: boolean;
}) {
  return (
    <div className="col gap-4">
      {/* current plan summary */}
      <div style={{
        border: '1px solid var(--live)', borderRadius: 6,
        background: 'var(--bg-1)', padding: 24,
        boxShadow: '0 0 24px var(--live-dim)',
      }}>
        <div className="flex between" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.1em' }}>
              [CURRENT PLAN]
            </div>
            <div style={{ fontSize: 28, fontWeight: 500, marginTop: 8, letterSpacing: '-0.02em', textTransform: 'capitalize' }}>
              {currentPlan} <span className="dim" style={{ fontSize: 14, fontWeight: 400 }}>
                {status?.subscription_id ? '· monthly' : '· no active subscription'}
              </span>
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              {status?.current_period_end
                ? `下次续费 · ${formatDate(status.current_period_end)} · 自动续 (你可以随时关掉)`
                : '当前未订阅 · 选择下方套餐升级'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono tiny dim" style={{ marginBottom: 4 }}>credits balance</div>
            <div style={{ fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', color: 'var(--live)' }}>
              {formatCents(status?.credits_cents ?? 0)}
            </div>
          </div>
        </div>

        <div className="flex" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {status?.subscription_id && (
            <button
              className="btn primary mono"
              onClick={onPortal}
              disabled={portalBusy}
            >
              {portalBusy ? '$ opening...' : '$ manage subscription'}
            </button>
          )}
          <button className="btn ghost mono" onClick={() => goTo('pricing')}>
            $ compare plans →
          </button>
        </div>
      </div>

      {/* plan tier cards */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {PLANS.map(p => {
          const isCurrent = p.id === currentPlan;
          const canCheckout = !!p.checkoutId && !isCurrent && stripeOk;
          const busy = busyPlan === p.checkoutId;
          return (
            <div key={p.id} style={{
              padding: 16,
              border: isCurrent ? '1px solid var(--live)' : '1px solid var(--line-2)',
              borderRadius: 6,
              background: 'var(--bg-1)',
              boxShadow: isCurrent ? '0 0 12px var(--live-dim)' : 'none',
              display: 'flex', flexDirection: 'column',
            }}>
              <div className="flex between" style={{ alignItems: 'center', marginBottom: 8 }}>
                <div className="mono tiny" style={{
                  color: isCurrent ? 'var(--live)' : 'var(--fg-2)',
                  letterSpacing: '0.1em',
                }}>
                  [{p.name.toUpperCase()}]
                </div>
                {isCurrent && (
                  <span className="tag mono tiny" style={{ borderColor: 'var(--live)', color: 'var(--live)' }}>
                    current
                  </span>
                )}
              </div>
              <div className="flex" style={{ alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 24, fontWeight: 500, letterSpacing: '-0.02em' }}>{p.price}</span>
                <span className="mono tiny dim">{p.priceUnit}</span>
              </div>
              <div className="meta" style={{ fontSize: 12, marginBottom: 12 }}>{p.blurb}</div>
              <ul style={{
                listStyle: 'none', padding: 0, margin: '0 0 16px',
                fontSize: 12, lineHeight: 1.7, color: 'var(--fg-1)',
              }}>
                {p.features.map(f => (
                  <li key={f}>· {f}</li>
                ))}
              </ul>
              <div style={{ marginTop: 'auto' }}>
                {isCurrent ? (
                  <button className="btn ghost mono tiny" disabled style={{ width: '100%' }}>
                    current plan
                  </button>
                ) : p.checkoutId ? (
                  <button
                    className="btn primary mono tiny"
                    style={{ width: '100%' }}
                    disabled={!canCheckout || busy}
                    onClick={() => p.checkoutId && onCheckout(p.checkoutId)}
                    title={!stripeOk ? 'Stripe not configured' : ''}
                  >
                    {busy ? '$ redirecting...' : `$ upgrade to ${p.name.toLowerCase()} →`}
                  </button>
                ) : (
                  <button className="btn ghost mono tiny" disabled style={{ width: '100%' }}>
                    free tier
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        padding: 16, border: '1px dashed var(--line-2)',
        borderRadius: 6, background: 'var(--bg-1)',
      }}>
        <div className="mono tiny" style={{ color: 'var(--judge)', letterSpacing: '0.1em', marginBottom: 6 }}>
          [HEADS UP · OVERAGE POLICY]
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.6 }}>
          超过套餐内分钟数后, 默认按 <span className="mono" style={{ color: 'var(--live)' }}>$0.04 / min</span> 自动按量计费, 在下次账单上加。
          <br />
          你可以在 <a className="link" onClick={() => goTo('settings')}>Settings · LLM</a> 里设月度预算上限, 触顶后自动停。
        </div>
      </div>
    </div>
  );
}
